/**
 * Unified course authoring shell — Details | Content | Settings (Publish)
 * Works for both create (no courseId) and edit (with courseId).
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Course, CourseStatus, CourseVisibility, CourseVersionSummary, Workspace } from '@/lib/types';
import SectionManager from '@/components/courses/SectionManager';
import CoverImagePicker from '@/components/courses/CoverImagePicker';
import AssignmentManager from '@/components/courses/AssignmentManager';

type Tab = 'details' | 'content' | 'assignments' | 'settings';

const STATUS_BADGE: Record<CourseStatus, string> = {
  draft: 'bg-amber-100 text-amber-800 ring-amber-200',
  in_review: 'bg-violet-100 text-violet-800 ring-violet-200',
  published: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  archived: 'bg-slate-100 text-slate-600 ring-slate-200',
};

export default function CourseEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const courseId = id ? parseInt(id, 10) : undefined;
  const isCreate = !courseId;

  const tabParam = searchParams.get('tab') as Tab | null;
  const [activeTab, setActiveTab] = useState<Tab>(tabParam || (isCreate ? 'details' : 'details'));

  const [course, setCourse] = useState<Course | null>(null);
  const [formData, setFormData] = useState({ title: '', description: '', cover_image_url: '' });
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [versions, setVersions] = useState<CourseVersionSummary[]>([]);
  const [visibility, setVisibility] = useState<CourseVisibility>('public');
  const [audienceIds, setAudienceIds] = useState<number[]>([]);
  const [orgWorkspaces, setOrgWorkspaces] = useState<Workspace[]>([]);
  const [savingAccess, setSavingAccess] = useState(false);
  const [passScore, setPassScore] = useState('50');
  const [meritScore, setMeritScore] = useState('80');
  const [savingCertSettings, setSavingCertSettings] = useState(false);

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const loadCourse = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    const res = await apiClient.getCourse(courseId);
    if (res.success && res.data) {
      const c = res.data as Course;
      setCourse(c);
      setFormData({
        title: c.title,
        description: c.description,
        cover_image_url: c.cover_image_url || '',
      });
      setVisibility((c.visibility as CourseVisibility) || 'public');
      setAudienceIds(c.audience_workspace_ids || []);
      setPassScore(String(c.pass_score ?? 50));
      setMeritScore(String(c.merit_score ?? 80));
      const vRes = await apiClient.getCourseVersions(courseId);
      if (vRes.success && vRes.data) {
        const items = Array.isArray(vRes.data) ? vRes.data : (vRes.data as any)?.results || [];
        setVersions(items);
      }
      const orgRes = await apiClient.ensurePersonalOrganization();
      if (orgRes.success && orgRes.data?.id) {
        const wsRes = await apiClient.getWorkspaces(orgRes.data.id);
        const items = Array.isArray(wsRes.data) ? wsRes.data : (wsRes.data as any)?.results || [];
        setOrgWorkspaces(items);
      }
    } else {
      setError('Failed to load course');
    }
    setLoading(false);
  }, [courseId]);

  useEffect(() => {
    if (!isCreate) loadCourse();
  }, [isCreate, loadCourse]);

  const handleSaveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Title and description are required.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    const payload = {
      title: formData.title,
      description: formData.description,
      cover_image_url: formData.cover_image_url || null,
    };
    let res;
    if (isCreate) {
      res = await apiClient.createCourse(payload);
    } else {
      res = await apiClient.updateCourse(courseId!, payload);
    }
    setSaving(false);
    if (res.success && res.data) {
      if (isCreate) {
        navigate(`/courses/${res.data.id}/edit?tab=content`);
      } else {
        setCourse(res.data as Course);
        setSuccessMsg('Details saved.');
      }
    } else {
      setError(res.error || 'Failed to save.');
    }
  };

  const handleLifecycle = async (
    action: 'publish' | 'unpublish' | 'archive' | 'restore' | 'submit' | 'clone'
  ) => {
    if (!courseId) return;
    setSaving(true);
    setError(null);
    let res;
    if (action === 'publish') res = await apiClient.publishCourse(courseId);
    else if (action === 'unpublish') res = await apiClient.unpublishCourse(courseId);
    else if (action === 'archive') res = await apiClient.archiveCourse(courseId);
    else if (action === 'restore') res = await apiClient.restoreCourse(courseId);
    else if (action === 'submit') res = await apiClient.submitCourseForReview(courseId);
    else res = await apiClient.cloneCourse(courseId);
    setSaving(false);
    if (res.success) {
      if (action === 'clone' && res.data?.id) {
        navigate(`/courses/${res.data.id}/edit?tab=details`);
        return;
      }
      await loadCourse();
      setSuccessMsg(`Course ${action === 'submit' ? 'submitted for review' : action + 'd'} successfully.`);
    } else {
      setError(res.error || `Failed to ${action} course.`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  const tabs: { key: Tab; label: string; disabled?: boolean }[] = [
    { key: 'details', label: 'Details' },
    { key: 'content', label: 'Content', disabled: isCreate },
    { key: 'assignments', label: 'Assignments', disabled: isCreate },
    { key: 'settings', label: 'Publish & Settings', disabled: isCreate },
  ];

  return (
    <div
      className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${
        activeTab === 'content'
          ? 'flex min-h-0 flex-col py-4 lg:h-full lg:overflow-hidden'
          : 'py-8'
      }`}
    >
      {/* Header */}
      <div className={`flex items-start justify-between gap-4 shrink-0 ${activeTab === 'content' ? 'mb-4' : 'mb-6'}`}>
        <div>
          <p className="text-sm font-medium uppercase tracking-wide text-amber-700">
            {isCreate ? 'New Course' : 'Edit Course'}
          </p>
          <h1 className="mt-1 text-xl font-bold text-slate-900">
            {course?.title || 'Untitled Course'}
          </h1>
          {course && (
            <span className={`mt-1.5 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_BADGE[course.status]}`}>
              {course.status.charAt(0).toUpperCase() + course.status.slice(1)}
            </span>
          )}
        </div>
        <button
          onClick={() => navigate('/courses')}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
        >
          ← Back to Courses
        </button>
      </div>

      {/* Tab Bar */}
      <div className={`border-b border-slate-200 shrink-0 ${activeTab === 'content' ? 'mb-4' : 'mb-6'}`}>
        <nav className="-mb-px flex gap-6">
          {tabs.map(({ key, label, disabled }) => (
            <button
              key={key}
              onClick={() => !disabled && switchTab(key)}
              disabled={disabled}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-blue-600 text-blue-600'
                  : disabled
                  ? 'border-transparent text-slate-300 cursor-not-allowed'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 shrink-0 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
          <button className="ml-3 underline" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {successMsg && (
        <div className="mb-4 shrink-0 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
          {successMsg}
          <button className="ml-3 underline" onClick={() => setSuccessMsg(null)}>Dismiss</button>
        </div>
      )}

      {/* ── Tab: Details ── */}
      {activeTab === 'details' && (
        <div className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm max-w-2xl">
          <h2 className="text-lg font-semibold text-slate-900 mb-5">Course Details</h2>
          <form onSubmit={handleSaveDetails} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Course Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Introduction to Python"
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                rows={5}
                placeholder="Describe what learners will gain from this course…"
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <CoverImagePicker
              value={formData.cover_image_url}
              onChange={url => setFormData(p => ({ ...p, cover_image_url: url }))}
            />
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {saving ? 'Saving…' : isCreate ? 'Create & Continue →' : 'Save Details'}
              </button>
              {!isCreate && (
                <button
                  type="button"
                  onClick={() => switchTab('content')}
                  className="rounded-lg border border-slate-200 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Go to Content →
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* ── Tab: Content ── */}
      {activeTab === 'content' && courseId && (
        <div className="min-h-0 flex-1 overflow-hidden lg:h-full">
          <SectionManager courseId={courseId} />
        </div>
      )}

      {activeTab === 'assignments' && courseId && (
        <AssignmentManager courseId={courseId} />
      )}

      {/* ── Tab: Settings / Publish ── */}
      {activeTab === 'settings' && courseId && course && (
        <div className="max-w-xl space-y-6">
          <div className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Who can enroll</h2>
              <p className="text-sm text-slate-500">
                Public courses are open to any signed-in student. Private courses require a workspace invite.
              </p>
            </div>
            <div className="flex gap-3">
              {(['public', 'private'] as CourseVisibility[]).map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setVisibility(v)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold capitalize transition-colors ${
                    visibility === v
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            {visibility === 'private' && (
              <div>
                <p className="text-xs font-medium text-slate-600 mb-2">Audience workspaces</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {orgWorkspaces.length === 0 ? (
                    <p className="text-xs text-slate-400">
                      No workspaces yet. Create one under Spaces, then return here.
                    </p>
                  ) : (
                    orgWorkspaces.map(ws => (
                      <label key={ws.id} className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={audienceIds.includes(ws.id)}
                          onChange={e => {
                            setAudienceIds(prev =>
                              e.target.checked
                                ? [...prev, ws.id]
                                : prev.filter(id => id !== ws.id)
                            );
                          }}
                        />
                        {ws.name}
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
            <button
              type="button"
              disabled={savingAccess}
              onClick={async () => {
                setSavingAccess(true);
                setError(null);
                const res = await apiClient.updateCourse(courseId, {
                  visibility,
                  audience_workspace_ids: visibility === 'private' ? audienceIds : [],
                  organization: course.organization || undefined,
                });
                setSavingAccess(false);
                if (res.success && res.data) {
                  setCourse(res.data as Course);
                  setSuccessMsg('Enrollment access saved.');
                } else {
                  setError(res.error || 'Failed to save access settings');
                }
              }}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {savingAccess ? 'Saving…' : 'Save access settings'}
            </button>
          </div>

          <div className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Certificates</h2>
              <p className="text-sm text-slate-500">
                Pass mark applies when this course has an exam or assignment. Merit is awarded above the pass mark.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm text-slate-700">
                Pass score (%)
                <input
                  type="number"
                  min={0}
                  max={99}
                  value={passScore}
                  onChange={(e) => setPassScore(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </label>
              <label className="text-sm text-slate-700">
                Merit score (%)
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={meritScore}
                  onChange={(e) => setMeritScore(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </label>
            </div>
            <button
              type="button"
              disabled={savingCertSettings}
              onClick={async () => {
                setSavingCertSettings(true);
                setError(null);
                const res = await apiClient.updateCourse(courseId, {
                  pass_score: Number(passScore),
                  merit_score: Number(meritScore),
                });
                setSavingCertSettings(false);
                if (res.success && res.data) {
                  setCourse(res.data as Course);
                  setSuccessMsg('Certificate thresholds saved.');
                } else {
                  setError(res.error || 'Failed to save certificate settings');
                }
              }}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {savingCertSettings ? 'Saving…' : 'Save certificate settings'}
            </button>
          </div>

          <div className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Lifecycle</h2>
            <p className="text-sm text-slate-500 mb-5">Control publish state, review, and archival.</p>

            <div className="space-y-3">
              {course.status === 'draft' && (
                <ActionCard
                  title="Submit for review"
                  description="Send to an admin for approval before publishing."
                  buttonLabel="Submit"
                  buttonClass="bg-violet-600 hover:bg-violet-700"
                  disabled={saving}
                  onClick={() => handleLifecycle('submit')}
                />
              )}
              {course.status === 'in_review' && (
                <p className="text-sm text-violet-700 bg-violet-50 border border-violet-100 rounded-lg px-3 py-2">
                  Waiting for admin approval…
                </p>
              )}
              {(course.status === 'draft' || course.status === 'in_review') && (
                <ActionCard
                  title="Publish now"
                  description="Publish immediately (creates a version snapshot)."
                  buttonLabel="Publish"
                  buttonClass="bg-emerald-600 hover:bg-emerald-700"
                  disabled={saving}
                  onClick={() => handleLifecycle('publish')}
                />
              )}
              {course.status === 'published' && (
                <ActionCard
                  title="Unpublish Course"
                  description="Hide from students and return to draft state."
                  buttonLabel="Unpublish"
                  buttonClass="bg-amber-600 hover:bg-amber-700"
                  disabled={saving}
                  onClick={() => handleLifecycle('unpublish')}
                />
              )}
              {course.status === 'archived' && (
                <ActionCard
                  title="Restore Course"
                  description="Bring this course back to draft for editing."
                  buttonLabel="Restore"
                  buttonClass="bg-blue-600 hover:bg-blue-700"
                  disabled={saving}
                  onClick={() => handleLifecycle('restore')}
                />
              )}
              {course.status !== 'archived' && (
                <ActionCard
                  title="Archive Course"
                  description="Mark as archived. Students cannot enroll or see it."
                  buttonLabel="Archive"
                  buttonClass="bg-slate-600 hover:bg-slate-700"
                  disabled={saving}
                  onClick={() => {
                    if (confirm('Archive this course? Students will lose access.')) {
                      handleLifecycle('archive');
                    }
                  }}
                />
              )}
              <ActionCard
                title="Clone Course"
                description="Duplicate this course (sections, lessons, blocks) as a new draft."
                buttonLabel="Clone"
                buttonClass="bg-slate-800 hover:bg-slate-900"
                disabled={saving}
                onClick={() => handleLifecycle('clone')}
              />
            </div>

            {course.review_note && (
              <p className="mt-4 text-xs text-red-600">Review note: {course.review_note}</p>
            )}
            {course.published_at && (
              <p className="mt-4 text-xs text-slate-400">
                Last published: {new Date(course.published_at).toLocaleDateString()}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Version history</h2>
            <p className="text-sm text-slate-500 mb-4">Snapshots created each time you publish.</p>
            {versions.length === 0 ? (
              <p className="text-xs text-slate-400">No published versions yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {versions.map(v => (
                  <li key={v.id} className="py-2 flex justify-between gap-3 text-sm">
                    <div>
                      <p className="font-medium text-slate-800">v{v.version_number}</p>
                      <p className="text-xs text-slate-400">
                        {v.sections_count ?? 0} sections · {v.lessons_count ?? 0} lessons
                        {v.created_by_name ? ` · ${v.created_by_name}` : ''}
                      </p>
                    </div>
                    <span className="text-xs text-slate-400 shrink-0">
                      {new Date(v.created_at).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Preview</h2>
            <p className="text-sm text-slate-500 mb-4">See this course as a student would.</p>
            <a
              href={`/courses/${courseId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors"
            >
              Open preview ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionCard({
  title,
  description,
  buttonLabel,
  buttonClass,
  onClick,
  disabled,
}: {
  title: string;
  description: string;
  buttonLabel: string;
  buttonClass: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
      <button
        onClick={onClick}
        disabled={disabled}
        className={`shrink-0 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-colors disabled:opacity-50 ${buttonClass}`}
      >
        {buttonLabel}
      </button>
    </div>
  );
}
